import { AppStateContext } from "@app/app/context/AppStateContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogHeightModes,
    DialogTitle,
    DialogTrigger,
    DialogVerticalPositions,
} from "@app/componentLibrary/ui/dialog";
import { BookingWhizzMemberLoginForm } from "@app/loyalty/components/BookingWhizzMemberLoginForm";
import { BookingWhizzMemberProfile } from "@app/loyalty/components/BookingWhizzMemberProfile";
import { BookingWhizzMemberRegisterForm } from "@app/loyalty/components/BookingWhizzMemberRegisterForm";
import { BookingWhizzMemberResetPasswordForm } from "@app/loyalty/components/BookingWhizzMemberResetPasswordForm";
import { LoyaltyContext } from "@app/loyalty/context/LoyaltyContext";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { Button } from "@ui";
import React, { Fragment, useContext } from "react";
import { useTranslation } from "react-i18next";

export interface IBookingWhizzMemberModalProps {
    triggerButton?: React.ReactNode;
    open?: boolean;
    onClose?: () => void;
    isMainModal?: boolean;
}

export const BookingWhizzMemberModal = ({ triggerButton, open, onClose, isMainModal }: IBookingWhizzMemberModalProps) => {
    const { index, memberDetails, setIndex } = useContext(LoyaltyContext);
    const { isMainLoyaltyModalOpen, setIsMainLoyaltyModalOpen } = useContext(AppStateContext);
    const { t } = useTranslation(["vendorLoyalty", "navigation"]);

    const handleClose = (backupClose?: () => void) => {
        if (isMainModal) {
            setIsMainLoyaltyModalOpen(false);
        }

        if (onClose) {
            onClose();
            return;
        }

        if (backupClose) {
            backupClose();
        }
    };

    return (
        <Dialog open={(isMainModal && isMainLoyaltyModalOpen) || open} onOpenChange={(isOpen) => !isOpen && handleClose(onClose)}>
            {triggerButton && <DialogTrigger asChild>{triggerButton}</DialogTrigger>}
            <DialogContent verticalPosition={DialogVerticalPositions.Bottom} heightMode={DialogHeightModes.Full}>
                <DialogHeader>
                    <DialogTitle>
                        {index === "reset_password" && (
                            <Button
                                data-testid="navigate-back-btn"
                                className="p-0 active:bg-white hover:bg-white hover:underline underline-offset-4 h-auto"
                                icon={ChevronLeftIcon}
                                iconPosition="left"
                                onClick={() => setIndex("login")}
                                variant="ghost"
                            >
                                <span className="ml-2">{t("back", { ns: "navigation" })}</span>
                            </Button>
                        )}

                        {["login", "register"].includes(index) && t("modal.title")}

                        {index === "profile" &&
                            `${t("modal.titleSignedIn")} ${memberDetails?.title ? `${memberDetails.title}.` : ""} ${
                                memberDetails?.name || "Member"
                            }`}
                    </DialogTitle>
                </DialogHeader>
                <Fragment>
                    <div className="p-4 w-full">
                        {/* Login & Register */}
                        {["login", "register"].includes(index) && (
                            <Fragment>
                                <div className="w-full bg-gray-200 rounded-lg p-2 mb-4 flex gap-y-4 inline-flex">
                                    <button
                                        className={`p-1 w-full rounded-lg ${index === "login" ? "bg-white" : ""}`}
                                        onClick={() => setIndex("login")}
                                    >
                                        {t("modal.loginTab")}
                                    </button>
                                    <button
                                        className={`p-1 w-full rounded-lg ${index === "register" ? "bg-white" : ""}`}
                                        onClick={() => setIndex("register")}
                                    >
                                        {t("modal.registerTab")}
                                    </button>
                                </div>

                                <div className="w-full max-h-fit">
                                    {/* Login form */}
                                    {index === "login" && <BookingWhizzMemberLoginForm></BookingWhizzMemberLoginForm>}

                                    {/* Register form */}
                                    {index === "register" && <BookingWhizzMemberRegisterForm></BookingWhizzMemberRegisterForm>}
                                </div>
                            </Fragment>
                        )}

                        {/* Reset password */}
                        {index === "reset_password" && <BookingWhizzMemberResetPasswordForm></BookingWhizzMemberResetPasswordForm>}

                        {/* Profile */}
                        {index === "profile" && <BookingWhizzMemberProfile></BookingWhizzMemberProfile>}
                    </div>
                </Fragment>
            </DialogContent>
        </Dialog>
    );
};
